import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose, extend, size } from 'underscore';
import { get, set } from '@bingads-webui/cookie';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withSiteMap } from '@bingads-webui-campaign-react/with-site-map';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import { BackboneInstanceWrapper } from '@bingads-webui-react/backbone-instance-wrapper';
import { perfMarkerPropTypes } from '@bingads-webui/performance-marker';
import { fetchInitData } from './repository';
import { loadLegacyDeps, createCommonDeps } from './utils';
import { getMultiAccountGoogleImportStyle } from './styles/multi-account-google-import-page-style';

const multiAcountScheduleImportTaskItemId = 'multi-account-scheduled-import-task-item-id';

class MultiAccountGoogleImport extends React.PureComponent {
  constructor(props) {
    super(props);
    this.perfMarker = props.perfMarker;

    if (props.serverContext && props.serverContext.CurrentAccount) {
      // eslint-disable-next-line no-param-reassign
      props.serverContext.CurrentAccount.AdvertiserCustomerID = null;
      // Note: this is needed, because for multi-account, we should never use CurrentAccount.AdvertiserCustomerID in API request.
      // Set this to null, and later on odata's withContextArgs extension will use CurrentCustomer.ID instead.
    }
    this.initialize(props);
  }

  initialize(props) {
    this.perfMarker.willFetchData();
    const fetchInitDataPromise = fetchInitData(props.scope.values, true);

    this.MultiAccountGoogleImportPageView = React.lazy(() => fetchInitDataPromise.then((data) => {
      const deps = this.createDeps(data, props);
      return loadLegacyDeps(props).then(spaFactory => spaFactory.initGoogleImportPage(deps));
    })
      .then(view => view.render())
      .then((instance) => {
        instance.startPollingAfterContinueImport();
        return {
          default: () => (
            <BackboneInstanceWrapper
              instance={instance}
            />),
        };
      }));
  }

  createDeps(data, props) {
    let taskItemId = get(multiAcountScheduleImportTaskItemId);
    if (size(taskItemId)) {
      taskItemId = parseInt(taskItemId, 10);
      set(multiAcountScheduleImportTaskItemId, '');
    }

    const deps = createCommonDeps({
      ...props,
      data,
    });
    extend(deps, {
      showPayment: false,
      importId: null,
      taskItemId,
      uxMode: 'main',
      isMultiAccountImport: true,
      perfMarker: this.perfMarker,
    });

    return deps;
  }

  render() {
    return (
      <React.Suspense fallback={<ProgressiveLoadingBlock />}>
        <div className={classNames(this.props.classes.pageContainer, this.props.classes.googleImportPage, this.props.classes.multiAccountGoogleImportPage, 'magi-page-container')}>
          <this.MultiAccountGoogleImportPageView />
        </div>
      </React.Suspense>
    );
  }
}

MultiAccountGoogleImport.propTypes = {
  serverContext: PropTypes.shape({
    CurrentAccount: PropTypes.object.isRequired,
    CurrentCustomer: PropTypes.object.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    pageContainer: PropTypes.string.isRequired,
    googleImportPage: PropTypes.string.isRequired,
    multiAccountGoogleImportPage: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  permissions: PropTypes.shape({
    IsUserContentAllowed: PropTypes.bool.isRequired,
  }).isRequired,
  perfMarker: perfMarkerPropTypes.isRequired,
};

export const StyledMultiAccountGoogleImport = compose(
  withI18nConsumer,
  WrappedComponent => withSiteMap(withDefaultStyles(WrappedComponent, getMultiAccountGoogleImportStyle))
)(MultiAccountGoogleImport);
